import { convertServerToLocalTime } from "../../utils/date"
import { credentialGatedIds } from "../../constants/gatewayRegistry"

// Copies the backend's per-row credential flags through to the grid. DERIVED from the registry,
// so a new gateway's flag can no longer silently become `undefined` here (which reads as "not
// configured" and hides the option — one of the two bugs the LawPay rollout hit).
// Both shapes are copied: the generic `gatewayFlags` map new code reads, and the legacy
// `<id>Configured` booleans still present for the transition.
//
// These are RUNTIME, per-request facts and stay on the wire — the static registry only says
// WHICH gateways are credential-gated on this rail, never whether the credentials are present.
const copyGatewayFlags = (item, rail) => {
  const out = { gatewayFlags: item?.gatewayFlags || {} }
  for (const id of credentialGatedIds(rail)) out[`${id}Configured`] = item?.[`${id}Configured`]
  return out
}

// Formats a billing address for the Billing tab grid. Kept the historical name.
export const formateAddress = ({ billingAddress }) => {
  if (!billingAddress) {
    return '-'
  }

  const { address, address2, city, state, zipcode } = billingAddress

  return `${address || address2}, ${city}, ${state} ${zipcode}`
}

// Maps the /cards/users/:id and /bankaccounts/users/:id list responses into
// DataGrid rows for TableListMethod. Every field the grid renders MUST be
// copied here — the grid only sees these rows, not the raw API items.
export const buildPaymentMethodRows = ({ cards, banks }) => ([
  ...(cards ?? []).map(item => ({
    createdAt: convertServerToLocalTime(item?.createdAt) ?? 'N/A',
    id: item?._id,
    type: item?.__t,
    name: item?.provider,
    isPrimary: item?.isPrimary,
    lastNumber: '****' + item?.cardNumberLast4,
    processor: item?.processor,
    // Which gateway approved the $1-2 check at enrolment. Deliberately a separate field from
    // `processor` above: that one routes charges, this one is a record of what worked once.
    cardVerification: item?.cardVerification || null,
    // The kill switch, per row. Without it the CRM offers a control that puts a real charge on a
    // client's card while the feature is switched off everywhere else.
    cardVerificationEnabled: Boolean(item?.cardVerificationEnabled),
    // Auto/Review selector reads this per row; dropping it made every account
    // display 'Review' (and auto accounts could never be switched back).
    processingMode: item?.processingMode,
    // LawPay is dual-rail, so it is offered on CARD rows too (WS 1281562) — the only
    // gateway flag that belongs in this branch. green.money/sola are ACH-only and must
    // never appear on a card, so the registry's card rail leaves them out.
    ...copyGatewayFlags(item, 'card'),
    // WS 2122673: the gateway the company default decides for this rail, or null (the account decides).
    companyDefaultGateway: item?.companyDefaultGateway ?? null,
    address: formateAddress({ billingAddress: item.billingAddress })
  })),
  ...(banks ?? []).map(item => ({
    createdAt: convertServerToLocalTime(item?.createdAt) ?? 'N/A',
    id: item?._id,
    type: 'Bank Account',
    name: item?.bankName,
    isPrimary: item?.isPrimary,
    processor: item?.processor,
    processingMode: item?.processingMode,
    // The NACHA unauthorized-return block. Cards never carry one (the set-site gates on the
    // BankAccount discriminator), so it belongs to this branch only. The grid needs the whole
    // subdocument, not just a boolean: blockedAt decides whether the row reads as blocked, and
    // the clear action has to refuse an authorization dated before it.
    debitBlock: item?.debitBlock,
    ...copyGatewayFlags(item, 'ach'),
    companyDefaultGateway: item?.companyDefaultGateway ?? null,
    lastNumber: '****' + item?.accountNumberLast4,
    address: formateAddress({ billingAddress: item.billingAddress })
  }))
])
